"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";

import { DictionaryProps } from "@/types";
import { ContainerFull, Image } from "@/components";
import { ExternalLayout, HeaderLoggedOut } from "@/layout";
import * as S from "./styles";
import Mockup1 from "@/assets/images/Mockup1.png";
import Mockup2 from "@/assets/images/Mobile-Mockup.png";
import DP01 from "@/assets/images/DP01.jpg";
import DP02 from "@/assets/images/DP02.jpg";
import DP03 from "@/assets/images/DP03.jpg";
import { ArrowRight, X, Check, Star } from "lucide-react";

const Landing: React.FC<DictionaryProps> = ({ dict }) => {
  const router = useRouter();
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");
  const pricingRef = React.useRef<HTMLDivElement>(null);

  const testimonials = [
    {
      img: DP01,
      name: "João",
      shop: "Barbearia Estilo",
      text: "Desde que comecei a usar o Striva, economizo 10 horas por semana e meus lucros aumentaram 25%!",
    },
    {
      img: DP02,
      name: "Maria",
      shop: "Barbearia Modern",
      text: "O Striva acelerou nosso atendimento e melhorou o controle financeiro.",
    },
    {
      img: DP03,
      name: "Carlos",
      shop: "Barbearia Tradicional",
      text: "Sistema fácil e completo, recomendo para qualquer barbearia.",
    },
  ];

  const plans = [
    {
      title: "INICIANTE",
      range: "1 a 2 barbeiros",
      monthly: 99.9,
      annual: {
        original: 1198,
        installments: 12,
        installmentValue: 74.99,
        total: 899.9,
      },
    },
    {
      title: "PROFISSIONAL",
      range: "até 5 barbeiros",
      monthly: 129.9,
      annual: {
        original: 1558,
        installments: 12,
        installmentValue: 91.66,
        total: 1099.9,
      },
    },
    {
      title: "PREMIUM",
      range: "até 10 barbeiros",
      monthly: 239.9,
      annual: {
        original: 2878,
        installments: 12,
        installmentValue: 174.99,
        total: 2099.9,
      },
    },
  ];
  // labels above will be run through `dict` when rendered so we can provide
  // translations without duplicating the data here.

  return (
    <ExternalLayout>
      <HeaderLoggedOut dict={dict} showAuthButtons />
      <ContainerFull className="dark:text-white">
        <div>
          <S.Hero>
            <S.Title className="max-w-4xl w-full mx-auto">
              {dict["Turn your barbershop into a money-making machine."] ||
                "Turn your barbershop into a money-making machine."}
            </S.Title>
            <S.Subtitle className="text-xl max-w-xl w-full mx-auto">
              {dict[
                "Simplify management, build customer loyalty, and multiply your profits with the #1 barber shop system in Brazil."
              ] ||
                "Simplify management, build customer loyalty, and multiply your profits with the #1 barber shop system in Brazil."}
            </S.Subtitle>
            <S.Subtitle className="text-xl max-w-xl w-full mx-auto">
              {dict[
                "Over 5,000 barbershops across Brazil rely on Striva for their management."
              ] ||
                "Over 5,000 barbershops across Brazil rely on Striva for their management."}
            </S.Subtitle>
            <a
              href="https://wa.me/5511918456133"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500 mt-8 shadow-2xl hover:bg-blue-700 duration-300 ease-in-out text-white font-bold text-xl py-3 px-5 rounded-4xl cursor-pointer animate-pulse"
            >
              {dict["Speak to a consultant."] || "Speak to a consultant."}
            </a>
          </S.Hero>

          <S.Hero>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex flex-col gap-4 justify-start">
                <S.Title className="max-w-md w-full text-left">
                  {dict["Everything your barbershop needs in one place"] ||
                    "Everything your barbershop needs in one place"}
                </S.Title>

                <p className="pr-0 md:pr-5 text-left mt-6">
                  {dict[
                    "Simplified financial management: control your finances clearly and see real results from your work"
                  ] ||
                    "Simplified financial management: control your finances clearly and see real results from your work"}
                </p>

                <p className="pr-0 md:pr-5 text-left">
                  {dict[
                    "Smart schedule: Manage times, avoid delays, and maximize your productivity."
                  ] ||
                    "Smart schedule: Manage times, avoid delays, and maximize your productivity."}
                </p>

                <p className="pr-0 md:pr-5 text-left">
                  {dict[
                    "Customer Loyalty: Send automatic reminders and promotions via WhatsApp."
                  ] ||
                    "Customer Loyalty: Send automatic reminders and promotions via WhatsApp."}
                </p>

                <p className="pr-0 md:pr-5 text-left">
                  {dict[
                    "Stock control: Monitor products accurately and avoid waste."
                  ] ||
                    "Stock control: Monitor products accurately and avoid waste."}
                </p>
              </div>
              <Image src={Mockup1} className="w-full h-auto" />
            </div>
          </S.Hero>

          <S.Hero>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex flex-col gap-4 justify-start">
                <S.Title className="max-w-md w-full text-left">
                  {dict[
                    "Don't wait until the end of the month to know what you've earned"
                  ] ||
                    "Don't wait until the end of the month to know what you've earned"}
                </S.Title>

                <p className="pr-5 bg-blue-600 text-white rounded-3xl w-full md:w-1/2 text-center px-2 py-2 mt-6 ">
                  {dict[
                    "Instant notifications: Appointments, commissions, and promotions directly on your phone"
                  ] ||
                    "Instant notifications: Appointments, commissions, and promotions directly on your phone"}
                </p>

                <p className="pr-5 bg-blue-600 text-white rounded-3xl w-full md:w-1/2 text-center px-2 py-2">
                  {dict[
                    "Total schedule control: See available and confirmed times in real-time."
                  ] ||
                    "Total schedule control: See available and confirmed times in real-time."}
                </p>

                <p className="pr-5 bg-blue-600 text-white rounded-3xl w-full md:w-1/2 text-center px-2 py-2">
                  {dict[
                    "Clear commissions: Know exactly how much each barber earned per service."
                  ] ||
                    "Clear commissions: Know exactly how much each barber earned per service."}
                </p>
              </div>
              <Image src={Mockup2} className="w-full h-auto" />
            </div>
          </S.Hero>

          <S.Hero className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-800 text-gray-50">
            <S.Title className="">
              {dict["Por que escolher o Striva?"] ||
                "Por que escolher o Striva?"}
            </S.Title>

            <p className="pr-5 text-left mt-6">
              {dict[
                "Com o Striva, você não só gerencia sua barbearia, mas transforma sua operação de forma 360º e Imediatamente!"
              ] ||
                "Com o Striva, você não só gerencia sua barbearia, mas transforma sua operação de forma 360º e Imediatamente!"}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6 p-6 px-10 bg-white justify-center border rounded-3xl border-gray-300">
              <div>
                <div className="w-16 text-left flex gap-2 bg-blue-600 shadow-2xl text-black text-lg items-center font-extrabold rounded-3xl p-1">
                  <ArrowRight className="p-1 font-extrabold text-black text-sm bg-white rounded-3xl  w-6 h-6" />
                  #1
                </div>
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Escolher o Melhor plano para o seu negócio"] ||
                    "Escolher o Melhor plano para o seu negócio"}
                </p>
              </div>

              <div>
                <div className="w-16 text-left flex gap-2 bg-blue-600 shadow-2xl text-black text-lg items-center font-extrabold rounded-3xl p-1">
                  <ArrowRight className="p-1 font-extrabold text-black text-sm bg-white rounded-3xl  w-6 h-6" />
                  #2
                </div>
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Cadastre Seus Barbeiros e Serviços"] ||
                    "Cadastre Seus Barbeiros e Serviços"}
                </p>
              </div>

              <div>
                <div className="w-16 text-left flex gap-2 bg-blue-600 shadow-2xl text-black text-lg items-center font-extrabold rounded-3xl p-1">
                  <ArrowRight className="p-1 font-extrabold text-black text-sm bg-white rounded-3xl  w-6 h-6" />
                  #3
                </div>
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Configure Sua Agenda Online"] ||
                    "Configure Sua Agenda Online"}
                </p>
              </div>

              <div>
                <div className="w-16 text-left flex gap-2 bg-blue-600 shadow-2xl text-black text-lg items-center font-extrabold rounded-3xl p-1">
                  <ArrowRight className="p-1 font-extrabold text-black text-sm bg-white rounded-3xl  w-6 h-6" />
                  #4
                </div>
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Acompanhe em Tempo Real e na sua mão!"] ||
                    "Acompanhe em Tempo Real e na sua mão!"}
                </p>
              </div>

              <div>
                <div className="w-16 text-left flex gap-2 bg-blue-600 shadow-2xl text-black text-lg items-center font-extrabold rounded-3xl p-1">
                  <ArrowRight className="p-1 font-extrabold text-black text-sm bg-white rounded-3xl  w-6 h-6" />
                  #5
                </div>
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Veja Seu Negócio Crescer"] ||
                    "Veja Seu Negócio Crescer"}
                </p>
              </div>

              <div className="">
                <p className="w-full md:w-1/2 text-left mt-3 font-extrabold text-black">
                  {dict["Pronto para escalar?"] || "Pronto para escalar?"}
                </p>
                <button
                  className="bg-blue-600 mt-2 hover:bg-blue-700 duration-300 ease-in-out text-white px-4 py-2 font-bold text-lg rounded-3xl cursor-pointer shadow-2xl"
                  onClick={() => {
                    pricingRef.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {dict["Assinar agora"] || "Assinar agora"}
                </button>
              </div>
            </div>
          </S.Hero>

          <S.Hero>
            <div className="flex flex-col gap-4 justify-start">
              <S.Title className="max-w-4xl w-full mx-auto text-center">
                {dict[
                  "Porque somos o mais completo sistema de gestão para barbearias."
                ] ||
                  "Porque somos o mais completo sistema de gestão para barbearias."}
              </S.Title>

              <p className="pr-0 md:pr-5 text-center mt-6">
                {dict[
                  "Garantimos que você terá tudo para subir o nível do seu negócio."
                ] ||
                  "Garantimos que você terá tudo para subir o nível do seu negócio."}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="border border-red-500 rounded-lg p-4">
                <h3 className="font-bold mb-4 text-2xl">
                  {dict["#Sistemas convencionais"] || "#Sistemas convencionais"}
                </h3>
                <ul className="space-y-1 text-lg px-2">
                  {[
                    "Gestão agendamento",
                    "Clube de assinatura",
                    "Conta digital",
                    "Notificado de vendas",
                    "Relatórios gerenciais",
                    "Aplicativo para colaborador",
                    "Lembrete de aniversario",
                    "Notificação de agendamento",
                    "Mensagens automáticas",
                    "Link de agendamento da sua barbearia",
                    "Aplicativo para sua barbearia",
                    "Agenda 24 horas disponível",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <X className="text-red-500 w-4 h-4" />
                      <span>{dict[item] || item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-green-500 rounded-lg p-4">
                <h3 className="font-bold mb-4 text-2xl">
                  {dict["#Striva"] || "#Striva"}
                </h3>
                <ul className="space-y-1 text-lg px-2">
                  {[
                    "Gestão agendamento",
                    "Clube de assinatura",
                    "Conta digital",
                    "Notificado de vendas",
                    "Relatórios gerenciais",
                    "Aplicativo para colaborador",
                    "Lembrete de aniversario",
                    "Notificação de agendamento",
                    "Mensagens automáticas",
                    "Link de agendamento da sua barbearia",
                    "Aplicativo para sua barbearia",
                    "Agenda 24 horas disponível",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <Check className="text-green-500 w-4 h-4" />
                      <span>{dict[item] || item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </S.Hero>

          {/* pricing section */}
          <S.Hero
            ref={pricingRef}
            className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-800 text-white"
          >
            <div className="text-center">
              <S.Title className="max-w-4xl w-full mx-auto">
                {dict["ESCOLHA O PLANO PERFEITO PARA SUA BARBEARIA!"] ||
                  "ESCOLHA O PLANO PERFEITO PARA SUA BARBEARIA!"}
              </S.Title>
              <p className="mt-2">
                {dict[
                  "Assine hoje e comece a transformar sua barbearia imediatamente."
                ] ||
                  "Assine hoje e comece a transformar sua barbearia imediatamente."}
              </p>

              <div className="inline-flex mt-6 rounded-full border border-blue-500 overflow-hidden shadow-md transition-transform duration-200 hover:scale-105">
                <button
                  className={`px-8 cursor-pointer py-2 font-semibold transition-colors duration-200 ${
                    cycle === "monthly"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-blue-500"
                  }`}
                  onClick={() => setCycle("monthly")}
                >
                  {dict["MENSAL"] || "MENSAL"}
                </button>
                <button
                  className={`px-8 py-2 cursor-pointer font-semibold transition-colors duration-200 ${
                    cycle === "annual"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-blue-500"
                  }`}
                  onClick={() => setCycle("annual")}
                >
                  {dict["ANUAL"] || "ANUAL"}
                </button>
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <div
                    key={plan.title}
                    className="bg-gray-800 rounded-lg p-6 flex flex-col items-center text-white transition-transform duration-300 hover:scale-105 border-2 border-blue-500"
                  >
                    <h3 className="text-xl font-bold uppercase">
                      {dict[plan.title] || plan.title}
                    </h3>
                    <p className="text-sm mt-1">
                      {dict[plan.range] || plan.range}
                    </p>

                    <div className="mt-4 text-3xl font-extrabold">
                      {cycle === "monthly" ? (
                        `R$${plan.monthly.toFixed(2)}`
                      ) : (
                        <>
                          <div className="text-sm mb-1">
                            <span className="line-through text-gray-400">
                              R${plan.annual.original.toFixed(2)}
                            </span>{" "}
                            por
                          </div>
                          <div>R${plan.annual.total.toFixed(2)}</div>
                        </>
                      )}
                    </div>

                    <button className="mt-6 bg-blue-600 hover:bg-blue-700 duration-300 ease-in-out text-white px-6 py-2 font-bold rounded-3xl cursor-pointer shadow-2xl transform transition hover:scale-105 animate-pulse">
                      {dict["Assinar agora"] || "Assinar agora"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </S.Hero>

          {/* post‑pricing hero with company info */}
          <S.Hero>
            <div className="text-center">
              <S.Title className="max-w-4xl w-full mx-auto">
                {dict["A Striva nasceu para barbeiros como você."] ||
                  "A Striva nasceu para barbeiros como você."}
              </S.Title>
              <p className="mt-4 max-w-2xl mx-auto">
                {dict[
                  "Desde barbearias iniciantes até grandes negócios Striva tem ajudado empreendedores a alcançar seus objetivos"
                ] ||
                  "Desde barbearias iniciantes até grandes negócios Striva tem ajudado empreendedores a alcançar seus objetivos"}
              </p>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 px-4">
                <div className="bg-white/10 rounded-lg p-6">
                  <p className="font-bold">
                    {dict["Feito por um barbeiro."] || "Feito por um barbeiro."}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-6">
                  <p className="font-bold">
                    {dict["Mais de 1.000 Barbearias!"] ||
                      "Mais de 1.000 Barbearias!"}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-6">
                  <p className="font-bold">
                    {dict["100 Milhões anuais movimentados."] ||
                      "100 Milhões anuais movimentados."}
                  </p>
                </div>
              </div>
            </div>
          </S.Hero>

          {/* testimonials hero */}
          <S.Hero className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-800 text-white">
            <S.Title className="text-center text-black">
              {dict["O que nossos clientes dizem sobre o Striva"] ||
                "O que nossos clientes dizem sobre o Striva"}
            </S.Title>
            <div className="text-center">
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 px-4">
                {testimonials.map((t) => (
                  <div
                    key={t.name}
                    className="bg-white rounded-xl text-black shadow-lg p-6 text-left transition-transform duration-300 hover:scale-105"
                  >
                    <div className="flex items-center gap-4">
                      <Image
                        src={t.img}
                        className="hidden w-12 h-12 rounded-full border border-gray-200"
                      />
                      <div>
                        <p className="font-bold text-lg dark:text-black">
                          {t.name}, {t.shop}
                        </p>
                        <div className="flex text-yellow-400 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-gray-700 dark:text-gray-800">
                      "{dict[t.text] || t.text}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </S.Hero>
        </div>
      </ContainerFull>
    </ExternalLayout>
  );
};

export default Landing;
